<script setup lang="ts">
const { imageUrl } = defineProps<{
  imageUrl: string;
}>();

const emit = defineEmits<{
  (e: "click"): void;
  (e: "keydown"): void;
}>();

const TOTAL_IMAGES = 36;
const PHYSICS_SENSITIVITY = 5;

const MIN_ZOOM_SCALE = 1;
const MAX_ZOOM_SCALE = 3;
const SCALE_CONVERSION_FACTOR = 100;

const isLoading = ref(false);
const preloadedImages = ref<Map<number, string>>(new Map());
const loadedImageCount = ref(0);

const { isTouchDevice } = useDeviceInformation();

// Physics-based dragging while keeping slider control
const {
  currentImageIndex,
  isDragging,
  hasDragged,
  isAnimating,
  startDrag,
  onDrag,
  endDrag,
  stopAnimation,
} = use360Physics(TOTAL_IMAGES, 0, PHYSICS_SENSITIVITY, isTouchDevice);

const currentScale = ref(1);

const shouldShowLoading = computed(() => {
  return isLoading.value;
});

const shouldShowSlider = computed(() => {
  return isReady.value && !isDragging.value && !isAnimating.value;
});

const currentImageUrl = computed(() => {
  const index = Number(currentImageIndex.value);
  const preloadedUrl = preloadedImages.value.get(index);

  if (preloadedUrl) {
    return preloadedUrl;
  }

  // Don't show original URLs while loading - wait for all images to be preloaded
  if (isLoading.value || loadedImageCount.value === 0) {
    return "";
  }

  const imageNumber = String(index + 1).padStart(2, "0");

  return `${imageUrl}${imageNumber}.jpg`;
});

const isReady = computed(() => {
  return loadedImageCount.value > 0 && !isLoading.value;
});

const preloadImages = async () => {
  isLoading.value = true;

  // Load all images at once - simpler and more predictable
  const loadImageAsBlob = (index: number): Promise<void> => {
    return new Promise((resolve) => {
      const imageNumber = String(index + 1).padStart(2, "0");
      const url = `${imageUrl}${imageNumber}.jpg`;

      if (!url) {
        resolve();

        return;
      }

      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (ctx) {
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const blobUrl = URL.createObjectURL(blob);
                preloadedImages.value.set(index, blobUrl);
                loadedImageCount.value++;
              }
              resolve();
            },
            "image/jpeg",
            1,
          );
        } else {
          // Fallback: use original URL
          preloadedImages.value.set(index, url);
          loadedImageCount.value++;
          resolve();
        }
      };

      img.onerror = () => {
        resolve();
      };

      img.src = url;
    });
  };

  // Load all images in parallel
  const allIndexes = Array.from({ length: TOTAL_IMAGES }, (_, i) => i);

  // only preload the first 4 and last 4 images
  const preloadedIndexes = [
    ...allIndexes.slice(0, 4),
    ...allIndexes.slice(-3),
    TOTAL_IMAGES,
  ];

  await Promise.allSettled(preloadedIndexes.map(loadImageAsBlob));

  isLoading.value = false;

  const restLoad = allIndexes.filter((i) => !preloadedIndexes.includes(i));

  await Promise.allSettled(restLoad.map(loadImageAsBlob));
};

const handleImageClick = () => {
  if (!hasDragged.value) {
    emit("click");
  }
};

const handleMouseDown = (event: MouseEvent) => {
  event.preventDefault();
  startDrag(event.clientX);
  document.body.style.cursor = "grabbing";
};

const handleMouseMove = (event: MouseEvent) => {
  if (!isDragging.value) {
    return;
  }

  event.preventDefault();
  onDrag(event.clientX);
};

const handleMouseUp = () => {
  if (isDragging.value) {
    endDrag();
    document!.body!.style!.cursor = "";
  }
};

const lastTouchMoveTime = ref(0);
const touchStartX = ref(0);
const touchStartY = ref(0);
const touchStartTime = ref(0);
const hasDeterminedGesture = ref(false);

const handleImageTouchStart = (event: TouchEvent) => {
  if (event.touches.length > 1) {
    return; // Ignore multi-touch events
  }

  const touch = event.touches[0];
  if (touch) {
    touchStartX.value = touch.clientX;
    touchStartY.value = touch.clientY;
    touchStartTime.value = Date.now();
    hasDeterminedGesture.value = false;
  }
};

const handleTouchMove = (event: TouchEvent) => {
  if (event.touches.length > 1) {
    return; // Ignore multi-touch events
  }

  const touch = event.touches[0];
  const now = Date.now();

  if (touch && now - lastTouchMoveTime.value > 8) {
    const deltaX = Math.abs(touch.clientX - touchStartX.value);
    const deltaY = Math.abs(touch.clientY - touchStartY.value);
    const timeDelta = now - touchStartTime.value;

    // Only start drag and prevent scrolling if this is clearly a horizontal gesture
    if (
      !hasDeterminedGesture.value &&
      (deltaX > 5 || deltaY > 5 || timeDelta > 50)
    ) {
      hasDeterminedGesture.value = true;

      // If horizontal movement is dominant, start 360° rotation and prevent scrolling
      if (deltaX > deltaY && deltaX > 5) {
        startDrag(touchStartX.value);
        event.preventDefault();
      }
    }

    // Only continue drag if we're already dragging (horizontal gesture determined)
    if (isDragging.value) {
      event.preventDefault();
      lastTouchMoveTime.value = now;
      onDrag(touch.clientX);
    }
  }
};

const handleTouchEnd = () => {
  hasDeterminedGesture.value = false;
  endDrag();
};

const handleMouseLeave = () => {
  if (isDragging.value) {
    endDrag();
  }
  document!.body!.style!.cursor = "";
};

const handleContainerKeydown = () => {
  emit("keydown");
};

onMounted(() => {
  if (!isLoading.value && loadedImageCount.value === 0) {
    preloadImages();
  }
});

onUnmounted(() => {
  // Clean up blob URLs to prevent memory leaks
  preloadedImages.value.forEach((blobUrl) => {
    URL.revokeObjectURL(blobUrl);
  });

  // Reset cursor state
  document!.body!.style!.cursor = "";

  stopAnimation();
});

const updateScale = (newScale?: number | string) => {
  if (!newScale) return;

  currentScale.value = Number(newScale) / SCALE_CONVERSION_FACTOR;
};
</script>

<template>
  <div
    class="base-360-viewer"
    :class="{
      'is-loading': shouldShowLoading,
      'is-dragging': isDragging,
    }"
    tabindex="0"
    role="img"
    aria-label="360° Product View. Use arrow keys or slider to rotate."
    @keydown="handleContainerKeydown"
  >
    <div
      class="base-360-viewer__image-container"
      :class="{
        'base-360-viewer__image-container--zoomed': isTouchDevice,
      }"
      data-test="360-viewer-image"
    >
      <div
        v-if="shouldShowLoading"
        class="base-360-viewer__loading-container"
      >
        <BaseLoadingSpinner />
      </div>

      <div
        v-else
        class="base-360-viewer__zoom-container"
        :class="{
          'cursor-grab': !isDragging,
          'cursor-grabbing': isDragging,
        }"
        data-test="360-viewer-zoom-container"
        @click="handleImageClick"
        @keydown="handleImageClick"
        @mousemove="handleMouseMove"
        @mousedown="handleMouseDown"
        @mouseup="handleMouseUp"
        @mouseleave="handleMouseLeave"
        @blur="handleMouseLeave"
      >
        <ScrollZoomContainer
          v-model="currentScale"
          :min-scale="MIN_ZOOM_SCALE"
          :max-scale="MAX_ZOOM_SCALE"
        >
          <NuxtImg
            :src="currentImageUrl"
            alt="360° Product View - 360° view"
            class="base-360-viewer__image"
            draggable="false"
            @touchmove="handleTouchMove"
            @touchend.passive="handleTouchEnd"
            @touchstart.passive="handleImageTouchStart"
          />
        </ScrollZoomContainer>
      </div>

      <LazyProductMedia360ViewerSlider
        v-if="!shouldShowLoading"
        v-model="currentImageIndex"
        :hide-slider="!shouldShowSlider"
        orient="horizontal"
        :min="0"
        :max="TOTAL_IMAGES - 1"
        class="absolute bottom-0 left-1/2 -translate-x-1/2 z-10 transition-all duration-300 ease-in-out max-w-[80%]"
      >
        <template #firstLabel>0º</template>
        <template #secondLabel>360º</template>
      </LazyProductMedia360ViewerSlider>
      <LazyProductMedia360ViewerSlider
        v-if="!shouldShowLoading"
        :model-value="currentScale * SCALE_CONVERSION_FACTOR"
        :hide-slider="!shouldShowSlider"
        orient="vertical"
        :min="MIN_ZOOM_SCALE * SCALE_CONVERSION_FACTOR"
        :max="MAX_ZOOM_SCALE * SCALE_CONVERSION_FACTOR"
        class="absolute top-1/2 right-0 -translate-y-1/2 w-[54px] h-[60%] max-h-[320px] z-10 transition-all duration-300 ease-in-out"
        @update:model-value="updateScale"
      >
        <template #firstLabel>
          <BaseIcon
            name="zoomIn"
            :size="20"
          />
        </template>
        <template #secondLabel>
          <BaseIcon
            name="zoomOut"
            :size="20"
          />
        </template>
      </LazyProductMedia360ViewerSlider>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.base-360-viewer {
  @apply relative w-full overflow-hidden;

  &.is-loading {
    @apply cursor-wait;
  }

  &.is-dragging {
    @apply select-none;
  }

  &__image-container {
    @apply relative size-full flex items-center justify-center pb-0;

    &--zoomed {
      @apply pb-0;
    }
  }

  &__loading-container {
    @apply flex items-center justify-center;
    & .loading-spinner {
      @apply static;
      @apply flex-1;
    }
  }

  &__zoom-container {
    @apply w-full flex items-center justify-center overflow-hidden relative touch-pan-x touch-pan-y touch-pinch-zoom;

    &:active {
      @apply cursor-grabbing;
    }
  }

  &__mobile-zoom-container {
    @apply flex items-center justify-center relative overflow-visible !important;

    .base-360-viewer__image {
      @apply w-full object-contain select-none pointer-events-auto;
    }
  }

  &__image-zoom-wrapper {
    @apply flex items-center justify-center relative;
  }

  &__image {
    @apply w-full object-contain select-none pointer-events-auto;
    transform-origin: center center;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .base-360-viewer {
    &__image {
      @apply transition-none;
    }
  }
}

@media (hover: none) and (pointer: coarse) {
  .base-360-viewer {
    &__zoom-container {
      @apply cursor-auto;

      &:active {
        @apply cursor-auto;
      }
    }
  }
}
</style>
